/**
 * /auth/tokenlogin — consume a platform token-login link.
 * @file src/web/features/auth/pages/tokenlogin.tsx
 *
 * A platform admin generates a one-time "log in as professional" link
 * (see /platform/professionals). The link carries a short-lived (1h)
 * login token + user in its URL *hash* (never sent to the server). This
 * bare, public page decodes it, stores the session, and drops into the
 * app as that professional — so it can be opened in a separate browser
 * without disturbing the admin's own session.
 */

import { useEffect, useState } from 'react';
import { config } from '../config';

export default function ImpersonateConsume() {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const d = params.get('d');
      if (!d) { setError('This login link is missing its token.'); return; }
      const payload = JSON.parse(decodeURIComponent(escape(atob(d))));
      if (!payload?.token || !payload?.user) { setError('This login link is invalid.'); return; }
      localStorage.setItem(config.auth.storage.token, payload.token);
      localStorage.setItem(config.auth.storage.user, JSON.stringify(payload.user));
      // Hard redirect so AuthProvider re-initialises from storage cleanly.
      window.location.replace('/dashboard');
    } catch {
      setError('This login link is invalid or has been corrupted.');
    }
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-receipt-40 px-6">
      <div className="text-center">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-inkblack">Can't sign you in</h1>
            <p className="mt-2 max-w-sm text-sm text-inkblack-60">{error}</p>
            <a href="/login" className="mt-5 inline-flex rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white no-underline">
              Go to sign in
            </a>
          </>
        ) : (
          <>
            <div className="mx-auto size-8 animate-spin rounded-full border-2 border-receipt border-t-brand-600" />
            <p className="mt-4 text-sm text-inkblack-60">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
}
